import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Box } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { EyebrowLabel } from "../../../../components/UIToolkit/molecules/EyebrowLabel";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { LOCATION_STAFFING as S } from "../../../../theme/tokens";
import { V2_ADD_ROW_BUTTON_SX, V2_PANEL_HAIRLINE } from "./consts";
import { narrowWeekdayChips } from "./dayToggleCells";
import type { DayAmountRow } from "./locationConfigData";
import { StaffingTargetLineV2 } from "./StaffingTargetLineV2";
import type { StaffingTargetRowsCommonProps } from "./StaffingTargetRows";

/**
 * The v2 staffing targets, "How many people": flat in the panel that holds them, one line per day group (its days as
 * round chips, then Target and Required as compact steppers, then its remove), lines parted by hairlines, and "Add days
 * with different targets" under them. Every line has the same fixed-width parts, so the lines' columns line up without
 * a shared grid, wrapped or not. Same props and handlers as v1; only the markup differs, and one rule: a day another
 * group holds can still be picked here, its chip marked red as in two groups, and Save says so until it is taken off
 * one of them (`shiftConfigProblemV2`). So a new group can always be added, even once every day has one.
 */
export const StaffingTargetRowsV2: FC<StaffingTargetRowsCommonProps & { disabled: boolean }> = ({
	rows,
	disabledDaysByRow,
	rowHasOccupiedDays,
	weekStartDay,
	onUpdateRow,
	onRemoveRow,
	onAddRow,
	disabled,
}): ReactElement => {
	const { t } = useTranslation();
	const chips = narrowWeekdayChips(weekStartDay);

	const renderLine = (row: DayAmountRow): ReactElement => (
		<StaffingTargetLineV2
			key={row.id}
			row={row}
			chips={chips}
			// DEFAULT: a row missing from the map has no days taken by other rows (as v1 reads it).
			heldElsewhere={disabledDaysByRow[row.id] ?? []}
			cannotRemove={rows.length <= 1 || rowHasOccupiedDays[row.id]}
			hasOccupiedDays={rowHasOccupiedDays[row.id]}
			onUpdateRow={onUpdateRow}
			onRemoveRow={onRemoveRow}
			disabled={disabled}
		/>
	);

	return (
		<Box data-testid="location-staffing-targets" sx={{ display: "flex", flexDirection: "column", width: "100%", minWidth: 0 }}>
			<EyebrowLabel component="h3" sx={{ mb: `${S.HEADING_GAP}px` }}>
				{t("schedule.template.editor.targets.title")}
			</EyebrowLabel>
			<Box>{rows.map(renderLine)}</Box>
			<Box sx={{ borderTop: V2_PANEL_HAIRLINE, pt: `${S.ADD_ROW_PAD_TOP}px` }}>
				<SystemButton
					buttonVariant="borderless"
					size="compact"
					noFullWidthMobile
					leadingIcon={<AddRoundedIcon fontSize="small" />}
					disabled={disabled}
					onClick={(e) => {
						e.stopPropagation();
						onAddRow();
					}}
					sx={V2_ADD_ROW_BUTTON_SX}
					data-testid="location-staffing-target-add-button"
				>
					{t("schedule.template.editor.targets.addGroup")}
				</SystemButton>
			</Box>
		</Box>
	);
};
